import { useState } from 'react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    contactMethod: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: formData.message,
        contactMethod: formData.contactMethod
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5l6um5porqtj2nscjs0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        // Redirect to thank you page
        window.location.href = '/thank-you-contact';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gray-50 rounded-2xl md:rounded-3xl p-6 sm:p-8 md:p-12 shadow-xl">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
              Send Us a Message
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600">
              Fill out the form below and we'll get back to you within 24 hours
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6" data-readdy-form>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              <div className={`transform transition-all duration-300 ${focusedField === 'name' ? 'scale-[1.02]' : ''}`}>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-[#4A90E2]">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3 sm:py-3.5 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-[#4A90E2] outline-none transition-all min-h-[48px] bg-white focus:shadow-lg"
                  placeholder="John Smith"
                />
              </div>

              <div className={`transform transition-all duration-300 ${focusedField === 'email' ? 'scale-[1.02]' : ''}`}>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-[#4A90E2]">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3 sm:py-3.5 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-[#4A90E2] outline-none transition-all min-h-[48px] bg-white focus:shadow-lg"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
              <div className={`transform transition-all duration-300 ${focusedField === 'phone' ? 'scale-[1.02]' : ''}`}>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-[#4A90E2]">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className="w-full px-4 py-3 sm:py-3.5 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-[#4A90E2] outline-none transition-all min-h-[48px] bg-white focus:shadow-lg"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className={`transform transition-all duration-300 ${focusedField === 'company' ? 'scale-[1.02]' : ''}`}>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('company')}
                  onBlur={() => setFocusedField(null)}
                  className="w-full px-4 py-3 sm:py-3.5 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-[#4A90E2] outline-none transition-all min-h-[48px] bg-white focus:shadow-lg"
                  placeholder="Your Company"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Preferred Contact Method <span className="text-[#4A90E2]">*</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {[
                  { value: 'email', icon: 'ri-mail-line', label: 'Email' },
                  { value: 'phone', icon: 'ri-phone-line', label: 'Phone' },
                  { value: 'either', icon: 'ri-chat-smile-3-line', label: 'Either' }
                ].map((method) => (
                  <label
                    key={method.value}
                    className={`flex-1 flex items-center justify-center px-4 py-3 border-2 rounded-xl cursor-pointer transition-all duration-300 min-h-[48px] transform hover:scale-105 ${
                      formData.contactMethod === method.value
                        ? 'border-[#4A90E2] bg-[#4A90E2]/5 shadow-lg scale-105'
                        : 'border-gray-300 bg-white hover:border-[#4A90E2]/50 hover:shadow-md'
                    }`}
                  >
                    <input
                      type="radio"
                      name="contactMethod"
                      value={method.value}
                      checked={formData.contactMethod === method.value}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <i className={`${method.icon} text-xl mr-2 transition-colors duration-300 ${
                      formData.contactMethod === method.value ? 'text-[#4A90E2]' : 'text-gray-500'
                    }`}></i>
                    <span className={`text-sm sm:text-base font-medium transition-colors duration-300 ${
                      formData.contactMethod === method.value ? 'text-[#2B5BA6]' : 'text-gray-700'
                    }`}>
                      {method.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className={`transform transition-all duration-300 ${focusedField === 'message' ? 'scale-[1.02]' : ''}`}>
              <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                Your Message <span className="text-[#4A90E2]">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                onFocus={() => setFocusedField('message')}
                onBlur={() => setFocusedField(null)}
                required
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-[#4A90E2] outline-none transition-all resize-none bg-white focus:shadow-lg"
                placeholder="Tell us about your energy needs..."
              ></textarea>
              <p className="text-xs text-gray-500 mt-2">{formData.message.length}/500 characters</p>
            </div>

            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl animate-shake">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-2xl text-red-600 mr-3"></i>
                  <p className="text-red-800 font-medium">
                    Something went wrong. Please try again or call us directly.
                  </p>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-4 sm:py-5 bg-[#2B5BA6] text-white text-base sm:text-lg font-bold rounded-xl hover:bg-[#234a8a] active:bg-[#1e3d73] transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg hover:shadow-2xl whitespace-nowrap min-h-[56px] flex items-center justify-center space-x-2 transform hover:scale-[1.02] active:scale-[0.98] group"
            >
              <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
              <i className={`ri-send-plane-fill text-xl transition-transform duration-300 ${isSubmitting ? '' : 'group-hover:translate-x-1 group-hover:-translate-y-1'}`}></i>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
